import { FiGift, FiShield, FiClock, FiUsers, FiArrowRight, FiCreditCard } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function BonusPoolPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth();
  const ctaLabel = loading ? t('common.loading') : t('bonusPool.ctaPrimary');

  const handleJoin = () => {
    if (loading) {
      return;
    }
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location } });
      return;
    }
    navigate('/bonus-pool/rounds');
  };

  return (
    <div className="min-h-screen bg-surface text-slate-100">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8 md:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2 rounded-full border border-glass-border bg-surface/70 px-3 py-1 text-xs uppercase tracking-widest text-slate-400">
            <FiGift className="h-3.5 w-3.5 text-accent-soft" />
            {t('bonusPool.tag')}
          </div>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 rounded-xl border border-glass-border bg-surface/60 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:bg-surface-highlight"
          >
            {t('bonusPool.ctaSecondary')}
          </button>
        </div>

        <section className="relative overflow-hidden rounded-2xl border border-glass-border bg-glass p-6 md:p-8">
          <div
            className="absolute -top-24 -right-10 h-64 w-64 rounded-full bg-accent-strong/20 blur-3xl"
            aria-hidden="true"
          />
          <div
            className="absolute -bottom-28 left-10 h-56 w-56 rounded-full bg-accent-warm/10 blur-3xl"
            aria-hidden="true"
          />

          <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center">
            <div className="flex-1 space-y-4">
              <div className="space-y-2">
                <h1 className="text-3xl font-semibold text-white md:text-4xl">
                  {t('bonusPool.title')}
                </h1>
                <p className="text-sm text-slate-300 md:text-base max-w-2xl">
                  {t('bonusPool.subtitle')}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleJoin}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-accent-strong to-accent px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_20px_rgba(8,12,18,0.35)] transition-transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <FiArrowRight className="h-4 w-4" />
                  {ctaLabel}
                </button>
              </div>
            </div>

            <div className="w-full lg:w-80 space-y-4">
              <div className="rounded-2xl border border-glass-border bg-surface/70 p-4">
                <div className="text-xs uppercase tracking-widest text-slate-400">
                  {t('bonusPool.statusTitle')}
                </div>
                <div className="mt-3 space-y-3 text-sm text-slate-200">
                  <div className="flex items-center gap-2">
                    <FiClock className="h-4 w-4 text-accent-warm" />
                    <span>{t('bonusPool.statusNote')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FiShield className="h-4 w-4 text-accent-soft" />
                    <span>{t('bonusPool.statusSecure')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FiUsers className="h-4 w-4 text-accent" />
                    <span>{t('bonusPool.statusInvite')}</span>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-glass-border bg-surface/60 p-4 text-xs text-slate-400">
                {t('bonusPool.note')}
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-3">
          <div className="glass-card p-5 space-y-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-strong/20 text-accent-soft">
              <FiCreditCard className="h-5 w-5" />
            </div>
            <div className="text-lg font-semibold text-white">{t('bonusPool.cardWalletTitle')}</div>
            <p className="text-sm text-slate-300">{t('bonusPool.cardWalletDesc')}</p>
          </div>
          <div className="glass-card p-5 space-y-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-warm/20 text-accent-warm">
              <FiUsers className="h-5 w-5" />
            </div>
            <div className="text-lg font-semibold text-white">{t('bonusPool.cardInviteTitle')}</div>
            <p className="text-sm text-slate-300">{t('bonusPool.cardInviteDesc')}</p>
          </div>
          <div className="glass-card p-5 space-y-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-positive/20 text-positive">
              <FiClock className="h-5 w-5" />
            </div>
            <div className="text-lg font-semibold text-white">{t('bonusPool.cardPayoutTitle')}</div>
            <p className="text-sm text-slate-300">{t('bonusPool.cardPayoutDesc')}</p>
          </div>
        </section>

        <section className="glass-card p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
            <span className="h-2 w-2 rounded-full bg-accent-strong" />
            {t('bonusPool.stepsTitle')}
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-glass-border bg-surface/60 p-4">
              <div className="text-xs font-semibold text-accent-soft">01</div>
              <div className="mt-2 text-base font-semibold text-white">{t('bonusPool.stepJoinTitle')}</div>
              <p className="mt-2 text-sm text-slate-300">{t('bonusPool.stepJoinDesc')}</p>
            </div>
            <div className="rounded-xl border border-glass-border bg-surface/60 p-4">
              <div className="text-xs font-semibold text-accent-soft">02</div>
              <div className="mt-2 text-base font-semibold text-white">{t('bonusPool.stepWalletTitle')}</div>
              <p className="mt-2 text-sm text-slate-300">{t('bonusPool.stepWalletDesc')}</p>
            </div>
            <div className="rounded-xl border border-glass-border bg-surface/60 p-4">
              <div className="text-xs font-semibold text-accent-soft">03</div>
              <div className="mt-2 text-base font-semibold text-white">{t('bonusPool.stepResultTitle')}</div>
              <p className="mt-2 text-sm text-slate-300">{t('bonusPool.stepResultDesc')}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
